import config from '../config';
import Cookies from 'js-cookie';

class ModerationService {
    constructor() {
        this.baseUrl = config.backendUrl;
    }

    /**
     * Get authentication headers
     */
    getAuthHeaders() {
        const token = Cookies.get('authToken');
        return {
            'Authorization': token ? `Token ${token}` : '',
            'Content-Type': 'application/json',
        };
    }

    /**
     * Submit a content report
     * @param {Object} reportData - { target_type, target_id, reason, description }
     * @returns {Promise<Object>}
     */
    async submitReport(reportData) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(reportData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors de la soumission du signalement');
            }

            return await response.json();
        } catch (error) {
            console.error('Error submitting report:', error);
            throw error;
        }
    }

    /**
     * Check if user has already reported a specific target
     * @param {string} targetType - Type of target (product, message, profile, review)
     * @param {string} targetId - ID of the target
     * @returns {Promise<Object>} - { exists: boolean, report?: Object }
     */
    async checkExistingReport(targetType, targetId) {
        try {
            const response = await fetch(
                `${this.baseUrl}/reports/check_existing/?target_type=${targetType}&target_id=${targetId}`,
                {
                    method: 'GET',
                    headers: this.getAuthHeaders(),
                }
            );

            if (!response.ok) {
                throw new Error('Erreur lors de la vérification du signalement');
            }

            return await response.json();
        } catch (error) {
            console.error('Error checking existing report:', error);
            throw error;
        }
    }

    /**
     * Block a user
     * @param {string|number} blockedUserId - ID or UUID of user to block
     * @param {string} reason - Reason for blocking
     * @returns {Promise<Object>}
     */
    async blockUser(blockedUserId, reason = '') {
        try {
            const response = await fetch(`${this.baseUrl}/user-blocks/`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    blocked: blockedUserId,
                    reason: reason,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du blocage de l\'utilisateur');
            }

            return await response.json();
        } catch (error) {
            console.error('Error blocking user:', error);
            throw error;
        }
    }

    /**
     * Unblock a user
     * @param {string} blockId - ID of the block record
     * @returns {Promise<void>}
     */
    async unblockUser(blockId) {
        try {
            const response = await fetch(`${this.baseUrl}/user-blocks/${blockId}/`, {
                method: 'DELETE',
                headers: this.getAuthHeaders(),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors du déblocage de l\'utilisateur');
            }
        } catch (error) {
            console.error('Error unblocking user:', error);
            throw error;
        }
    }

    /**
     * Get list of blocked users
     * @returns {Promise<Array>}
     */
    async getBlockedUsers() {
        try {
            const response = await fetch(`${this.baseUrl}/user-blocks/`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            });

            if (!response.ok) {
                if (response.status === 404) {
                    console.warn('User blocks endpoint returned 404 - returning empty array');
                    return [];
                }
                throw new Error('Erreur lors de la récupération des utilisateurs bloqués');
            }

            const data = await response.json();
            // Handle both paginated (data.results) and direct array responses
            return Array.isArray(data) ? data : (data.results || []);
        } catch (error) {
            console.error('Error fetching blocked users:', error);
            // Return empty array instead of throwing to prevent UI crashes
            return [];
        }
    }

    /**
     * Get user's reports
     * @returns {Promise<Array>}
     */
    async getMyReports() {
        try {
            const response = await fetch(`${this.baseUrl}/reports/`, {
                method: 'GET',
                headers: this.getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la récupération des signalements');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching reports:', error);
            throw error;
        }
    }
}

export default new ModerationService();
